import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { fetchThemePreferences, saveThemePreferences } from '@/services/themePreferencesService';

export type ThemeMode = 'light' | 'dark';
export type ThemePalette = 'padrao' | 'rose' | 'pastel' | 'marinho';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const isSupabaseConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

interface ThemeContextType {
  mode: ThemeMode;
  palette: ThemePalette;
  toggleMode: () => void;
  setPalette: (palette: ThemePalette) => void;
  // Backward compatibility
  theme: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('themeMode');
    return (saved as ThemeMode) || 'dark';
  });

  const [palette, setPaletteState] = useState<ThemePalette>(() => {
    const saved = localStorage.getItem('themePalette');
    return (saved as ThemePalette) || 'padrao';
  });

  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const currentUserIdRef = useRef<string | null>(null);
  const modeRef = useRef<ThemeMode>(mode);
  const paletteRef = useRef<ThemePalette>(palette);

  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    paletteRef.current = palette;
  }, [palette]);

  const loadRemotePreferences = useCallback(async (userId: string) => {
    if (!isSupabaseConfigured || !userId) return;

    try {
      const remote = await fetchThemePreferences(userId);

      if (remote) {
        if (remote.mode && remote.mode !== modeRef.current) {
          setMode(remote.mode);
        }

        if (remote.palette && remote.palette !== paletteRef.current) {
          setPaletteState(remote.palette);
        }
      } else {
        await saveThemePreferences({ userId, mode: modeRef.current, palette: paletteRef.current });
      }
    } catch (error) {
      console.error('Failed to sync theme preferences', error);
    }
  }, []);

  const persistRemotePreferences = useCallback(async (next: Partial<{ mode: ThemeMode; palette: ThemePalette }>) => {
    const userId = currentUserIdRef.current;
    if (!isSupabaseConfigured || !userId) return;

    try {
      await saveThemePreferences({
        userId,
        mode: next.mode ?? modeRef.current,
        palette: next.palette ?? paletteRef.current,
      });
    } catch (error) {
      console.error('Failed to persist theme preferences', error);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
    localStorage.setItem('themePalette', palette);
    
    // Remove all theme classes
    document.documentElement.classList.remove(
      'light', 'dark', 
      'padrao', 'rose', 'pastel', 'marinho'
    );
    
    // Add current theme classes
    document.documentElement.classList.add(mode, palette);
    
    // Force theme update by injecting dynamic CSS
    let styleEl = document.getElementById('dynamic-theme-colors');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'dynamic-theme-colors';
      document.head.appendChild(styleEl);
    }
    
    // Inject CSS that forces color application
    styleEl.textContent = `
      body, #root {
        background-color: hsl(var(--background)) !important;
        color: hsl(var(--foreground)) !important;
      }
      * {
        border-color: hsl(var(--border)) !important;
      }
    `;
    
    console.log('🎨 Theme applied:', { mode, palette, classes: document.documentElement.className });
  }, [mode, palette]);

  useEffect(() => {
    if (!isSupabaseConfigured) return;

    let isMounted = true;

    const syncSession = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (!isMounted) return;

      if (error) {
        console.error('Failed to resolve Supabase session for theme sync', error);
        return;
      }

      setCurrentUserId(data.session?.user?.id ?? null);
    };

    syncSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;
      setCurrentUserId(session?.user?.id ?? null);
    });

    return () => {
      isMounted = false;
      authListener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!currentUserId) return;
    loadRemotePreferences(currentUserId);
  }, [currentUserId, loadRemotePreferences]);

  const toggleMode = () => {
    const nextMode = mode === 'dark' ? 'light' : 'dark';
    setMode(nextMode);
    void persistRemotePreferences({ mode: nextMode });
  };

  const setPalette = (newPalette: ThemePalette) => {
    setPaletteState(newPalette);
    void persistRemotePreferences({ palette: newPalette });
  };

  return (
    <ThemeContext.Provider value={{ 
      mode, 
      palette, 
      toggleMode, 
      setPalette,
      // Backward compatibility
      theme: mode,
      toggleTheme: toggleMode
    }}>
      {children}
    </ThemeContext.Provider>
  );
};